import React from 'react';
import { I18nextProvider } from 'react-i18next';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { i18n } from './main';
import { MapViewProvider, Map } from '@origo/map-context';
import { Loader } from '@geodata/georg';
import { useSynkAuth } from './auth/useSynkAuth';
import { ApiProvider } from './api/ApiContext';
import { MapPreviewProvider } from './map/MapPreviewContext';
import { MapPreviewLayer } from './map/MapPreviewLayer';
import AppLayout from './components/AppLayout';
import defaultSettings from './config/defaultSettings.json';

// Én QueryClient for app-levetiden. Retry holder spørringer robuste mot
// forbigående 5xx, men unngår retry-storm på mutasjoner.
const queryClient = new QueryClient({
    defaultOptions: {
        queries: { retry: 1, staleTime: 15_000 },
        mutations: { retry: 0 },
    },
});

interface AppSettings {
    webmap?: string;
    webscene?: string;
    portalUrl?: string;
    apiBaseUrl?: string;
    defaultSystem?: string;
}

const PORTAL_URL = (defaultSettings as AppSettings).portalUrl ?? 'https://www.arcgis.com';

const App: React.FC = () => {
    const { agolToken, settings, isReady, signOut } = useSynkAuth<AppSettings>(
        'synkadmin',
        defaultSettings as AppSettings,
        PORTAL_URL,
    );

    if (!isReady) {
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh',
                    flexDirection: 'column',
                    gap: '1rem',
                }}
            >
                <Loader />
            </div>
        );
    }

    return (
        <QueryClientProvider client={queryClient}>
            <ApiProvider
                agolToken={agolToken}
                defaultBaseUrl={settings?.apiBaseUrl ?? 'http://localhost:5022'}
            >
                <I18nextProvider i18n={i18n}>
                    <MapViewProvider>
                        <MapPreviewProvider>
                            <AppLayout
                                onSignOut={() => signOut()}
                                defaultSystem={settings?.defaultSystem ?? 'ngis'}
                                mapContent={
                                    <>
                                        <Map
                                            webMapId={settings?.webmap}
                                            webSceneId={settings?.webscene}
                                            tools2D={['fullscreen', 'scalebar', 'measure']}
                                            tools3D={['fullscreen', 'measure']}
                                            disableClick={false}
                                            disablePopup={false}
                                            disable3D={false}
                                            includeEiendomsTeigLayer={false}
                                            includeEiendomsGrenseLayer={false}
                                        />
                                        <MapPreviewLayer />
                                    </>
                                }
                            />
                        </MapPreviewProvider>
                    </MapViewProvider>
                </I18nextProvider>
            </ApiProvider>
        </QueryClientProvider>
    );
};

export default App;
